import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, Button, MenuItem, Select, Typography } from "@mui/material";
import { Field, Formik } from "formik";
import { object, string } from "yup";
import { ContactMessage } from "../../../api-types/contact";
import { useTransparentSelectStyles } from "../../../themes/style-hooks";
import { PageWrapper } from "../../app/container/PageWrapper";
import { selectMenuProps } from "../../professional/job-requests/search-form";
import { useUser } from "../../state/hooks";
import { useToast } from "../../utils/useToast";
import { ConfirmationDialog } from "../components";
import { CustomTextArea } from "../elements";
import { useAddMessage } from "../hooks/contact";
import { useResponsiveness } from "../hooks/utils/useResponsiveness";
import { useScopedDowngradedStateValue } from "../hooks/utils/useScopedDowngradedStateValue";
import { useMessageSentDialog } from "./state/hooks";

const initialValues: ContactMessage = {
  type: "comment",
  content: "",
  userId: "",
};

const validationSchema = object().shape({
  content: string().required("A message is required"),
});

export const Contact = () => {
  const transparentSelectStyles = useTransparentSelectStyles();
  const { notifyError } = useToast();
  const user = useUser();

  const { isSmall } = useResponsiveness();

  const { mutate: addMessage, isLoading: isAddingMessage } = useAddMessage();

  const messageSentDialog = useScopedDowngradedStateValue(
    useMessageSentDialog()
  );

  const setMessageSentDialog = useMessageSentDialog().set;

  const handleClose = () => {
    setMessageSentDialog({ open: false });
  };

  return (
    <PageWrapper>
      <Box
        display="flex"
        flexDirection="column"
        rowGap={12}
        pt={10}
        height="100%"
      >
        <Typography variant="h1">Contact Us</Typography>

        <Box display="flex" flexDirection="column" rowGap={2.5}>
          <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
            We Want To Hear From You
          </Typography>
          <Typography variant={"body1"}>
            Select whether you want to reach out to us about site functionality
            or about anything else at all. Thank you for getting in touch and
            for your feedback.
          </Typography>
        </Box>

        <Formik
          initialValues={initialValues}
          onSubmit={(values, { setFieldValue }) => {
            if (!user.id.value) return;

            addMessage(
              { ...values, userId: user.id.value },
              {
                onSuccess: () => {
                  setMessageSentDialog({ open: true });
                  setFieldValue("content", "");
                },
                onError: () => {
                  notifyError(
                    "There was an error sending your message. Please try again"
                  );
                },
              }
            );
          }}
          validationSchema={validationSchema}
        >
          {({ handleBlur, handleChange, handleSubmit, values, errors }) => {
            return (
              <form
                onSubmit={handleSubmit}
                style={{
                  height: "100%",
                }}
              >
                <Box
                  height="100%"
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                >
                  <Box>
                    <Select
                      autoWidth
                      className={transparentSelectStyles.root}
                      defaultValue={values.type}
                      disableUnderline
                      IconComponent={KeyboardArrowDownIcon}
                      name="type"
                      MenuProps={selectMenuProps}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      style={{
                        height: "auto",
                        width: isSmall ? 300 : 380,
                        top: -45,
                      }}
                      value={values.type}
                    >
                      <MenuItem value="comment">
                        Leaving a comment about this site
                      </MenuItem>
                      <MenuItem value="other">
                        Reaching out about something else
                      </MenuItem>
                    </Select>

                    <Box width="100%" pt={10}>
                      <Field
                        component={CustomTextArea}
                        maxRows={4}
                        minRows={3}
                        name="content"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        placeholder="Enter Text Here"
                        value={values.content}
                        error={errors.content}
                      />
                    </Box>
                  </Box>
                  <Box pt={10} pb={20} alignSelf="flex-end">
                    <Button
                      size="medium"
                      disabled={isAddingMessage || !user.id.value}
                      type="submit"
                      variant="contained"
                    >
                      Send
                    </Button>
                  </Box>
                </Box>
              </form>
            );
          }}
        </Formik>
      </Box>
      <ConfirmationDialog
        open={messageSentDialog.open}
        onClose={handleClose}
        cancelLabel="Close"
        title="Thank You For Reaching Out To Us"
        message="Your message has been sent."
      />
    </PageWrapper>
  );
};
